<template>
  <div class="shop-register" :style="{ paddingRight: cartWidth + 'px' }">

    <!-- FULL-WIDTH HEADER — ide iznad i kategorija i artikala -->
    <div class="top-hdr">
      <div class="mid-spacer"></div>

      <!-- Global Search — jedina tražilica -->
      <button class="search-global-btn" @click="globalSearchOpen = true">
        <div class="search-global-left">
          <v-icon size="14" color="#94A3B8">mdi-magnify</v-icon>
          <span>Pretraži cijelu bazu...</span>
        </div>
        <div class="search-global-right">
          <v-icon size="13" color="#B0BEC5">mdi-database-search-outline</v-icon>
          <kbd class="search-kbd">Ctrl K</kbd>
        </div>
      </button>
      <div class="pill-group" style="margin-left:6px;">
        <v-tooltip bottom max-width="200" color="white" content-class="vpc-tooltip">
          <template v-slot:activator="{ on }">
            <button class="pill pill--icon" :class="{ active: artViewMode === 'grid' }" v-on="on" @click="setArtViewMode('grid')">
              <v-icon size="15">mdi-view-grid-outline</v-icon>
            </button>
          </template>
          <span style="font-size:12px;color:#1e293b;padding:8px 10px;display:block;">Prikaz artikala u obliku kartica (grid)</span>
        </v-tooltip>
        <v-tooltip bottom max-width="200" color="white" content-class="vpc-tooltip">
          <template v-slot:activator="{ on }">
            <button class="pill pill--icon" :class="{ active: artViewMode === 'list' }" v-on="on" @click="setArtViewMode('list')">
              <v-icon size="15">mdi-format-list-bulleted</v-icon>
            </button>
          </template>
          <span style="font-size:12px;color:#1e293b;padding:8px 10px;display:block;">Prikaz artikala kao lista</span>
        </v-tooltip>
      </div>
      <div class="pill-group" style="margin-left:6px;">
        <v-tooltip bottom max-width="200" color="white" content-class="vpc-tooltip">
          <template v-slot:activator="{ on }">
            <button class="pill pill--icon" :disabled="artFontSize <= 10" v-on="on" @click="changeArtFontSize(-1)">
              <v-icon size="15">mdi-format-font-size-decrease</v-icon>
            </button>
          </template>
          <span style="font-size:12px;color:#1e293b;padding:8px 10px;display:block;">Smanji veličinu teksta artikala</span>
        </v-tooltip>
        <v-tooltip bottom max-width="200" color="white" content-class="vpc-tooltip">
          <template v-slot:activator="{ on }">
            <button class="pill pill--icon" :disabled="artFontSize >= 22" v-on="on" @click="changeArtFontSize(1)">
              <v-icon size="15">mdi-format-font-size-increase</v-icon>
            </button>
          </template>
          <span style="font-size:12px;color:#1e293b;padding:8px 10px;display:block;">Povećaj veličinu teksta artikala</span>
        </v-tooltip>
      </div>
    </div>

    <!-- SADRŽAJ: kategorije + artikli -->
    <div class="shop-content">

      <!-- KATEGORIJE -->
      <shop-categories
        :style="{ width: catWidth + 'px', minWidth: catWidth + 'px' }"
        :categories="categories"
        :selected-category-id="selectedCategoryId"
        :display-mode="catDisplayMode"
        :font-size="catFontSize"
        @select-category="onSelectCategory"
        @change-display-mode="setCatDisplayMode"
        @change-font-size="changeCatFontSize"
      />
      <v-tooltip right max-width="220" color="white" content-class="vpc-tooltip">
        <template v-slot:activator="{ on }">
          <div class="col-resize-handle" v-on="on" @mousedown.prevent="onColHandleMousedown('cat', $event)" />
        </template>
        <span style="font-size:12px;color:#1e293b;padding:8px 10px;display:block;line-height:1.6;">
          <strong style="display:block;margin-bottom:3px;">Promjena širine kategorija</strong>
          Povuci lijevo ili desno za prilagodbu. Postavka se pamti za ovu blagajnu.
        </span>
      </v-tooltip>

      <!-- SREDINA -->
      <div class="shop-mid" style="position:relative;">
        <!-- Loading overlay -->
        <div v-if="creatingReceipt" class="mid-loading-overlay">
          <div class="mid-loading-card">
            <v-progress-circular indeterminate color="#00BCD4" size="40" width="3" />
            <div class="mid-loading-amount">{{ receiptLoadingAmount }}</div>
            <div class="mid-loading-label">{{ paymentMethod === 'P' ? 'Kreiranje ponude...' : 'Kreiranje računa...' }}</div>
          </div>
        </div>

        <!-- Artikli -->
        <shop-articles
          :category-id="selectedCategoryId"
          :search-query="''"
          :view-mode="artViewMode"
          :cart-quantities="cartExpanded ? {} : cartQuantities"
          :wholesale-view="wholesaleView"
          :font-size="artFontSize"
          @add-item="onAddItem"
        />
      </div>

    </div>

    <!-- RESIZE HANDLE — lijeva granica košarice -->
    <v-tooltip v-if="!cartExpanded" left max-width="220" color="white" content-class="vpc-tooltip">
      <template v-slot:activator="{ on }">
        <div
          class="col-resize-handle col-resize-handle--cart"
          :style="{ right: cartWidth + 'px' }"
          v-on="on"
          @mousedown.prevent="onColHandleMousedown('cart', $event)"
        />
      </template>
      <span style="font-size:12px;color:#1e293b;padding:8px 10px;display:block;line-height:1.6;">
        <strong style="display:block;margin-bottom:3px;">Promjena širine košarice</strong>
        Povuci lijevo ili desno za prilagodbu. Postavka se pamti za ovu blagajnu.
      </span>
    </v-tooltip>

    <!-- KOŠARICA -->
    <shop-cart
      :cart-width="cartWidth"
      :cart="cart"
      :customer="customer"
      :discount="discount"
      :payment-method="paymentMethod"
      :receipt-type="receiptType"
      :currency="currency"
      :creating-receipt="creatingReceipt"
      :expanded="cartExpanded"
      :fiscalize="fiscalize"
      :has-certificate="hasCertificate"
      :eracun="eracun"
      :eracun-email="eracunEmail"
      :eracun-enabled="eracunEnabled"
      :custom-pnb="customPnb"
      :payment-reference="paymentReference"
      :available-payment-methods="availablePaymentMethods"
      :wholesale-view="wholesaleView"
      :eracun-active="eracun"
      :eracun-disabled-reason="eracunDisabledReason"
      :register-type="registerType"
      :eracun-block-reason="eracunBlockReason"
      @update-item-kpd="(idx, val) => $set(cart[idx], 'kpdcode', val)"
      :invoice-date="invoiceDate"
      :delivery-date="deliveryDate"
      :value-date="valueDate"
      :selected-payment-due="selectedPaymentDue"
      :payment-dues="paymentDues"
      @update:invoiceDate="v => { invoiceDate = v; updateValueDate() }"
      @update:deliveryDate="v => deliveryDate = v"
      @update:valueDate="v => valueDate = v"
      @update:selectedPaymentDue="v => { selectedPaymentDue = v; updateValueDate() }"
      @open-item-modal="openItemModal"
      @expand="onCartExpand"
      @open-customer-panel="customerPanelOpen = true"
      @open-discount-modal="onOpenDiscountModal"
      @open-note-modal="noteModalOpen = true"
      @open-payment-modal="paymentModalOpen = true"
      @open-write-in="writeInModalOpen = true"
      @open-close-balance="openCloseBalance"
      @toggle-wholesale="toggleWholesale"
      @remove-item="onItemRemove"
      @update-item-field="onUpdateItemField"
      @clear-cart="cart = []"
      @create-receipt="createReceipt"
      @update:paymentMethod="v => paymentMethod = v"
      @update:fiscalize="v => fiscalize = v"
      @update:eracun="v => { eracun = v; if (!v) eracunEmail = '' }"
      @update:eracunEmail="v => eracunEmail = v"
      @update:customPnb="v => { customPnb = v; if (!v) paymentReference = '' }"
      @update:paymentReference="v => paymentReference = v"
    />

    <!-- MODAL ZA RUČNI ARTIKL -->
    <shop-write-in-modal
      :open="writeInModalOpen"
      :register-type="registerType"
      @add-item="onAddItem"
      @close="writeInModalOpen = false"
    />

    <!-- MODAL ZA UNOS CIJENE (artikli s manual_price_input) -->
    <shop-manual-price-modal
      ref="manualPriceModal"
      :wholesale-view="wholesaleView"
      @add-item="onManualPriceConfirmed"
    />

    <!-- MODAL ZA PLAĆANJE -->
    <shop-payment-modal
      :open="paymentModalOpen"
      :register-type="registerType"
      :current-method="paymentMethod"
      :current-fiscalize="fiscalize"
      :current-eracun="eracun"
      :current-eracun-email="eracunEmail"
      :eracun-enabled="eracunEnabled"
      :eracun-disabled-reason="eracunDisabledReason"
      :eracun-kpd-warning="eracunKpdWarning"
      :has-certificate="hasCertificate"
      @create="onPaymentModalCreate"
      @update:currentMethod="v => paymentMethod = v"
      @update:fiscalize="v => fiscalize = v"
      @update:eracun="v => eracun = v"
      @update:eracunEmail="v => eracunEmail = v"
      @close="paymentModalOpen = false"
    />

    <!-- CUSTOMER PANEL -->
    <shop-customer-panel
      :open="customerPanelOpen"
      :current-customer="customer"
      @select-customer="onSelectCustomer"
      @close="customerPanelOpen = false"
    />

    <!-- MODALI -->
    <shop-item-modal
      :open="itemModalOpen"
      :item="editingItem"
      :index="editingIndex"
      :register-type="registerType"
      :wholesale-view="wholesaleView"
      @save="onItemSave"
      @remove="onItemRemove"
      @close="itemModalOpen = false"
    />

    <shop-note-modal
      :open="noteModalOpen"
      :note="receiptNote"
      @save="receiptNote = $event"
      @close="noteModalOpen = false"
    />

    <shop-discount-modal
      :open="discountModalOpen"
      :current-rate="currentDiscountRate"
      @save="onDiscountSave"
      @close="discountModalOpen = false"
    />

    <shop-pdf-viewer ref="pdfViewer" />

    <!-- ZAKLJUČAK BLAGAJNE (Z-izvještaj) -->
    <close-balance ref="closeBalance" />

    <shop-search-modal
      :open="globalSearchOpen"
      :wholesale-view="wholesaleView"
      @close="globalSearchOpen = false"
      @add-item="onAddItem"
    />

    <!-- Confirm modal — transakcijski/ponuda bez kupca -->
    <v-dialog v-model="noCustomerDialog" max-width="420" persistent>
      <v-card style="border-radius:14px; overflow:hidden;">
        <div style="padding:20px 20px 8px; display:flex; align-items:center; gap:10px;">
          <v-icon color="#f59e0b" size="22">mdi-account-question-outline</v-icon>
          <span style="font-size:15px; font-weight:700; color:#1e293b;">
            {{ paymentMethod === 'P' ? 'Ponuda bez kupca' : 'Transakcijski račun bez kupca' }}
          </span>
        </div>
        <div style="padding:8px 20px 20px; font-size:13px; color:#374151; line-height:1.7;">
          Nije odabran kupac. Želite li kreirati
          <strong>{{ paymentMethod === 'P' ? 'ponudu' : 'transakcijski račun' }}</strong>
          bez podataka o kupcu?
        </div>
        <div style="padding:0 16px 16px; display:flex; justify-content:flex-end; gap:8px;">
          <v-btn text @click="noCustomerDialog = false" style="font-size:13px;">Odustani</v-btn>
          <v-btn color="#00BCD4" dark depressed style="font-size:13px; border-radius:8px;" @click="noCustomerDialog = false; proceedCreateReceipt()">
            Kreiraj bez kupca
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import { df } from '@/plugins/firebase'
import state from '@/state'
import EventBus from '@/plugins/event-bus'
import { remapItems } from '@/libs/receiptIssuing/items'
import { getPayload } from '@/libs/receiptIssuing/payload'
import { sendHttp } from '@/libs/receiptIssuing/payment'
import { getPaymentMethod } from '@/libs/receiptIssuing/commons'
import ShopCategories from '@/modules/cash-register/components/shop-register/ShopCategories.vue'
import ShopArticles from '@/modules/cash-register/components/shop-register/ShopArticles.vue'
import ShopCart from '@/modules/cash-register/components/shop-register/ShopCart.vue'
import ShopPdfViewer from '@/modules/cash-register/components/shop-register/ShopPdfViewer.vue'
import ShopSearchModal from '@/modules/cash-register/components/shop-register/ShopSearchModal.vue'

function regKey (type) {
  const rid = state.getCashRegister()?.id || 'default'
  return `shop_${type}_${rid}`
}

export default {
  name: 'ShopRegister',

  inject: ['showMsgBox', 'confirm', 'confirmClose', 'showMsgBoxHtml', 'showLoader', 'hideLoader', 'openPdfPrint'],

  components: {
    ShopCategories,
    ShopArticles,
    ShopCart,
    ShopPdfViewer,
    ShopSearchModal,
    ShopPaymentModal: () => import('@/modules/cash-register/components/shop-register/ShopPaymentModal.vue'),
    ShopWriteInModal: () => import('@/modules/cash-register/components/shop-register/ShopWriteInModal.vue'),
    ShopManualPriceModal: () => import('@/modules/cash-register/components/shop-register/ShopManualPriceModal.vue'),
    ShopCartExpanded: () => import('@/modules/cash-register/components/shop-register/ShopCartExpanded.vue'),
    ShopCustomerPanel: () => import('@/modules/cash-register/components/shop-register/ShopCustomerPanel.vue'),
    ShopItemModal: () => import('@/modules/cash-register/components/shop-register/ShopItemModal.vue'),
    ShopNoteModal: () => import('@/modules/cash-register/components/shop-register/ShopNoteModal.vue'),
    ShopDiscountModal: () => import('@/modules/cash-register/components/shop-register/ShopDiscountModal.vue'),
    CloseBalance: () => import('@/modules/cash-register/components/closeBalance/CloseBalance.vue')
  },

  data () {
    return {
      // Kategorije
      categories: [],
      selectedCategoryId: '',
      catDisplayMode: (() => {
        const saved = localStorage.getItem(regKey('cat_display'))
        return (saved === 'icon' || saved === 'card') ? saved : 'card'
      })(),
      catDisplayModes: [
        { value: 'icon', label: '☰ ikona' },
        { value: 'card', label: '⊟ kartice' }
      ],
      catFontSize: parseInt(localStorage.getItem(regKey('cat_font_size')) || '14'),
      // Artikli
      searchQuery: '',
      artViewMode: localStorage.getItem(regKey('art_view')) || 'grid',
      artFontSize: parseInt(localStorage.getItem(regKey('art_font_size')) || '15'),
      // Košarica
      cart: [],
      customer: null,
      discount: null,
      paymentMethod: 'G',
      receiptType: 'R',
      receiptNote: '',
      creatingReceipt: false,
      // Modal za plaćanje
      paymentModalOpen: false,
      writeInModalOpen: false,
      wholesaleView: false, // inicijalizira se u mounted po blagajni
      // Datumi (T, V, P payment)
      invoiceDate: Date.now(),
      deliveryDate: Date.now(),
      valueDate: (() => {
        const d = new Date()
        d.setDate(d.getDate() + 8)
        return d.getTime()
      })(),
      selectedPaymentDue: 8,
      paymentDues: [1, 2, 3, 5, 6, 7, 8, 9, 10, 12, 14, 15, 30, 60, 120],
      // Napredne opcije (expanded)
      fiscalize: false, // inicijalizira se u mounted na temelju certifikata
      eracun: false,
      eracunEmail: '',
      customPnb: false,
      paymentReference: '',
      // UI state
      cartExpanded: false,
      customerPanelOpen: false,
      itemModalOpen: false,
      editingItem: null,
      editingIndex: -1,
      noteModalOpen: false,
      discountModalOpen: false,
      currentDiscountRate: 0,
      activeReceiptDiscountRate: 0, // aktivni % popust na račun
      // Resize stupaca
      catWidth: parseInt(localStorage.getItem(regKey('col_cat')) || '240'),
      cartWidth: parseInt(localStorage.getItem(regKey('col_cart')) || '360'),
      colDrag: null,
      // PDF viewer meta
      receiptDocMeta: null,
      globalSearchOpen: false,
      noCustomerDialog: false,
      // Firebase cleanup
      listeners: []
    }
  },

  computed: {
    currency () {
      return state.getCurrentCompany()?.currency || 'EUR'
    },
    location () { return state.getPointOfSale() },
    cashRegister () { return state.getCashRegister() },
    fullTotal () {
      // Puna cijena bez popusta (za fullTotal na backendu)
      return this.cart.reduce((s, i) => s + ((i.unitPrice || i.price || 0) * (i.quantity || 1)), 0)
    },
    total () {
      // Ukupno s per-item popustima (item.total već uključuje popust)
      return this.cart.reduce((s, i) => s + (i.total || 0), 0)
    },
    paymentType () {
      return { value: this.paymentMethod, type: this.receiptType }
    },
    selectedCategoryName () {
      const cat = this.categories.find(c => c.id === this.selectedCategoryId)
      return cat ? cat.name : ''
    },
    cartQuantities () {
      return this.cart.reduce((acc, item) => {
        acc[item.id] = (acc[item.id] || 0) + item.quantity
        return acc
      }, {})
    },
    receiptLoadingAmount () {
      const cents = Math.round(this.total)
      return (cents / 100).toFixed(2).replace('.', ',') + ' €'
    },
    registerType () {
      return state.getCashRegister()?.type || 'MP'
    },
    availablePaymentMethods () {
      const all = [
        { value: 'G', label: 'Gotovina', icon: 'mdi-cash-multiple' },
        { value: 'K', label: 'Kartica', icon: 'mdi-credit-card-outline' },
        { value: 'T', label: 'Transakcijski', icon: 'mdi-bank-outline' },
        { value: 'P', label: 'Ponuda', icon: 'mdi-file-document-outline' }
      ]
      if (this.registerType === 'MP') return all.filter(m => ['G', 'K'].includes(m.value))
      if (this.registerType === 'VP') return all.filter(m => ['T', 'P'].includes(m.value))
      return all
    },
    hasCertificate () {
      const company = state.getCurrentCompany()
      return company?.certificate !== undefined && company?.certificate !== null && company?.certificate !== ''
    },
    eracunProviderActive () {
      const company = state.getCurrentCompany()
      return !!(company?.eracun?.provider && company?.eracun?.active === true)
    },
    eracunEnabled () {
      if (!this.eracunProviderActive) return false
      // eRačun zahtijeva isključenu fiskalizaciju (isto kao CrudTemplate)
      if (this.fiscalize) return false
      if (!this.customer) return false
      const c = this.customer
      const hasData = (c.name || c.oib) && c.address && c.city && c.country
      return !!(hasData && c.country === 'Hrvatska')
    },
    eracunDisabledReason () {
      if (this.eracunEnabled) return ''
      const reasons = []
      if (!this.eracunProviderActive) reasons.push('Tvrtka nema aktiviranog davatelja usluge eRačuna.')
      if (this.fiscalize) reasons.push('Fiskalizacija mora biti isključena (Ne) za slanje eRačuna.')
      if (!this.customer) {
        reasons.push('Mora biti odabran kupac s potpunim podacima.')
      } else {
        const c = this.customer
        if (!((c.name || c.oib) && c.address && c.city && c.country)) {
          reasons.push('Podaci kupca su nepotpuni (potrebno: naziv/OIB, adresa, grad, država).')
        } else if (c.country !== 'Hrvatska') {
          reasons.push('eRačun je dostupan samo za kupce iz Hrvatske.')
        }
      }
      return reasons.join(' ')
    },
    allItemsHaveKpd () {
      if (!this.eracun) return true
      return this.cart.every(item => /^\d{2}\.\d{2}\.\d{2}$/.test(item.kpdcode || ''))
    },
    eracunKpdWarning () {
      const missing = this.cart.filter(item => !/^\d{2}\.\d{2}\.\d{2}$/.test(item.kpdcode || ''))
      if (!missing.length) return ''
      return `${missing.length} artikl${missing.length === 1 ? '' : 'a'} nema ispravnu KPD šifru (format: xx.xx.xx). Kliknite na artikl u košarici i dodajte KPD šifru.`
    },
    eracunBlockReason () {
      if (!this.eracun) return ''
      const reasons = []
      const missingKpd = this.cart.filter(item => !/^\d{2}\.\d{2}\.\d{2}$/.test(item.kpdcode || ''))
      if (missingKpd.length) {
        reasons.push(`${missingKpd.length} artikl${missingKpd.length === 1 ? '' : 'a'} nema ispravnu KPD šifru (format: xx.xx.xx). Kliknite na artikl u košarici i dodajte KPD šifru.`)
      }
      if (!this.eracunEmail || !/.+@.+\..+/.test(this.eracunEmail)) {
        reasons.push('Email adresa za eRačun nije upisana ili nije ispravna. Upišite email u polju ispod eRačun prekidača.')
      }
      return reasons.join('\n')
    }
  },

  watch: {
    cart (val) {
      window.__shopCartCount = val.length
    },
    eracun (val) {
      // Kada se uključi eRačun, povuci email kupca ako postoji
      if (val && !this.eracunEmail && this.customer?.email) {
        this.eracunEmail = this.customer.email
      }
    },
    customer (val) {
      // Kada se odabere kupac dok je eRačun uključen, popuni email
      if (this.eracun && val?.email && !this.eracunEmail) {
        this.eracunEmail = val.email
      }
    }
  },

  methods: {
    getCategories () {
      const listener = df
        .doc(`company_categories/${state.getCurrentCompany().id}`)
        .onSnapshot(doc => {
          if (doc && doc.data() && doc.data().categories) {
            this.categories = Object.keys(doc.data().categories)
              .map(key => doc.data().categories[key])
              .filter(c => c.status === 'OK')
              .sort((a, b) => a.name.localeCompare(b.name, 'hr'))
            if (this.categories.length && !this.selectedCategoryId) {
              this.selectedCategoryId = this.categories[0].id
            }
          }
        })
      this.listeners.push(listener)
    },

    onSelectCategory (id) {
      this.selectedCategoryId = id
      this.searchQuery = ''
    },

    setCatDisplayMode (mode) {
      this.catDisplayMode = mode
      localStorage.setItem(regKey('cat_display'), mode)
    },
    changeCatFontSize (delta) {
      this.catFontSize = Math.max(10, Math.min(20, this.catFontSize + delta))
      localStorage.setItem(regKey('cat_font_size'), this.catFontSize)
    },
    changeArtFontSize (delta) {
      this.artFontSize = Math.max(10, Math.min(22, this.artFontSize + delta))
      localStorage.setItem(regKey('art_font_size'), this.artFontSize)
    },
    setArtViewMode (mode) {
      this.artViewMode = mode
      localStorage.setItem(regKey('art_view'), mode)
    },

    _extractTax (product) {
      const cur = this.currency
      const taxes = product.prices?.[cur]?.taxes
      if (taxes && taxes.length > 0) {
        const t = taxes[0]
        // Rate može biti decimalni (0.25) ili basis points (2500)
        const rate = t.rate > 1 ? t.rate / 10000 : t.rate
        return { rate, name: `PDV ${Math.round(rate * 100)}%` }
      }
      return null
    },

    onAddItem (product) {
      const existing = this.cart.findIndex(i => i.id === product.id)
      if (existing === -1 && product.manual_price_input && !product.price) {
        const tax = this._extractTax(product)
        this.$refs.manualPriceModal.open(product, tax ? tax.rate : null)
        return
      }
      if (existing !== -1) {
        const item = this.cart[existing]
        const newQty = item.quantity + 1
        // Koristi discounted_price (već izračunat per-unit iznos) ako postoji
        const unitPrice = item.discounted_price != null ? item.discounted_price : (item.unitPrice || item.price)
        const newTotal = Math.round(unitPrice * newQty)
        this.$set(this.cart, existing, { ...item, quantity: newQty, total: newTotal })
      } else {
        const unitPrice = product.price
        const rate = this.activeReceiptDiscountRate
        const discountedUnit = rate > 0 ? Math.round(unitPrice * (1 - rate / 100)) : null
        const rateBp = rate > 0 ? Math.round(rate * 100) : null
        const tax = product.tax || this._extractTax(product)
        const cur = this.currency
        // Artikli s manual_price_input nemaju prices[currency] (cijena dolazi iz modala) —
        // dopuni ga (u kopiji, ne diramo katalošku referencu) da remapItems ne puca na item.prices[currency].price
        const prices = { ...(product.prices || {}) }
        if (!prices[cur]) {
          prices[cur] = {
            price: unitPrice,
            taxes: [{ rate: tax ? Math.round(tax.rate * 10000) : 2500, name: tax ? tax.name : 'PDV 25%' }]
          }
        }
        this.cart.unshift({
          id: product.id,
          name: product.name,
          quantity: 1,
          unitPrice,
          price: unitPrice,
          total: discountedUnit != null ? discountedUnit : unitPrice,
          unit: product.unit,
          selling_unit: product.selling_unit || product.unit || 'kom',
          prices,
          code: product.code || '',
          type: product.type || 'product',
          discount: rate > 0 ? { active: true, type: 'RATE', rate: rateBp, amount: unitPrice - discountedUnit, name: 'Popust' } : null,
          discounted_price: discountedUnit != null ? discountedUnit : undefined,
          note: '',
          tax,
          kpdcode: product.kpdcode || '',
          naknade: product.naknade || undefined
        })
      }
    },

    onManualPriceConfirmed ({ product, mpcCents, pdv }) {
      const tax = pdv ? { rate: pdv.rate / 10000, name: pdv.name } : undefined
      const cur = this.currency
      // Katalog manual_price_input artikala već ima prices[cur] (placeholder, npr. price:0),
      // pa onAddItem-ov "!prices[cur]" backfill preskoči override — force-postavi unesenu
      // cijenu/PDV ovdje da remapItems ne pošalje price:0/pdv.rate:0 na checkoutu.
      const prices = {
        ...(product.prices || {}),
        [cur]: {
          price: mpcCents,
          taxes: [{ rate: pdv ? pdv.rate : 2500, name: pdv ? pdv.name : 'PDV 25%' }]
        }
      }
      this.onAddItem({ ...product, price: mpcCents, tax, prices })
    },

    buildAutoPaymentReference (receiptNumber, cashRegister) {
      // Ista logika kao templatePayment.js#calculateReferenceNumber/getReferenceNumber
      // (koristi je CrudTemplateDialog) — slovo se pretvara u broj po abecedi (A=1..Z=26),
      // radi konzistentnosti formata poziva na broj kroz cijelu aplikaciju.
      const location = state.getPointOfSale()
      const sep = state.getCurrentCompany().receipt_numeration_separator
      let raw = `${receiptNumber}${sep}${location.location_id}${sep}${cashRegister.number}`
      raw = raw.replace(/[a-zA-Z]/g, c => c.toUpperCase().charCodeAt(0) - 64)
      raw = raw.replace(/[^0-9-]+/g, '')
      raw = raw.replaceAll('--', '-')
      // Slovo->broj konverzija zna napuhati segment (npr. "POSL33" -> "1615191233") —
      // ograniči svaki segment na max 4 znamenke da poziv na broj ostane razuman.
      raw = raw.split('-').map(segment => segment.slice(0, 4)).join('-')
      return `HR00 ${raw}`
    },

    openItemModal (index) {
      this.editingIndex = index
      this.editingItem = { ...this.cart[index] }
      this.itemModalOpen = true
    },

    onItemSave (index, changes) {
      const item = { ...this.cart[index], ...changes }
      const unitPrice = item.unitPrice || item.price

      if (item.discount?.type === 'RATE') {
        const ratePct = item.discount.rate // postotak (npr. 10)
        const rateBp = Math.round(ratePct * 100) // basis points za backend (1000)
        const discountedUnit = Math.round(unitPrice * (1 - ratePct / 100))
        item.total = discountedUnit * item.quantity
        item.discounted_price = discountedUnit
        // Normaliziraj discount objekt za backend (basis points + amount)
        item.discount = {
          active: true,
          type: 'RATE',
          rate: rateBp,
          amount: unitPrice - discountedUnit,
          name: 'Popust'
        }
      } else {
        item.total = unitPrice * item.quantity
        item.discounted_price = undefined
        item.discount = null
      }
      this.$set(this.cart, index, item)
    },

    onDiscountSave (d) {
      this.discount = null // nikad ne koristimo cart-level discount

      if (!d) {
        this.activeReceiptDiscountRate = 0
        // Ukloni popuste sa svih artikala
        this.cart = this.cart.map(item => {
          const unitPrice = item.unitPrice || item.price
          return {
            ...item,
            discount: null,
            discounted_price: undefined,
            total: unitPrice * item.quantity
          }
        })
        return
      }

      if (d.type === 'RATE') {
        this.activeReceiptDiscountRate = d.rate // spremi aktivni % popust
        // Raspodjeli postotak na svaki artikl
        // Backend očekuje rate u basis points (10000 = 100%), npr. 10% = 1000
        const rateBp = Math.round(d.rate * 100) // 10% → 1000 basis points
        this.cart = this.cart.map(item => {
          const unitPrice = item.unitPrice || item.price
          const discountedUnit = Math.round(unitPrice * (1 - d.rate / 100))
          const discountAmount = unitPrice - discountedUnit // apsolutni iznos popusta per unit (u centima)
          return {
            ...item,
            discount: {
              active: true,
              type: 'RATE',
              rate: rateBp,
              amount: discountAmount,
              name: 'Popust'
            },
            discounted_price: discountedUnit,
            total: discountedUnit * item.quantity
          }
        })
      }
    },

    onUpdateItemField (index, field, value) {
      const item = { ...this.cart[index], [field]: value }
      const unitPrice = item.unitPrice || item.price
      const base = unitPrice * item.quantity

      if (item.discount?.type === 'RATE') {
        // rate može biti postotak (10) ili basis points (1000) — normaliziraj
        let ratePct = item.discount.rate
        if (ratePct > 100) ratePct = ratePct / 100 // pretvori iz bp u postotak
        const discountedUnit = Math.round(unitPrice * (1 - ratePct / 100))
        item.total = discountedUnit * item.quantity
        item.discounted_price = discountedUnit
        item.discount = {
          active: true,
          type: 'RATE',
          rate: Math.round(ratePct * 100), // bp za backend
          amount: unitPrice - discountedUnit,
          name: 'Popust'
        }
      } else {
        item.total = base
        item.discounted_price = undefined
        item.discount = null
      }
      this.$set(this.cart, index, item)
    },

    onItemRemove (index) {
      this.cart.splice(index, 1)
      this.itemModalOpen = false
    },

    onSelectCustomer (c) {
      this.customer = c
    },

    onOpenDiscountModal () {
      this.currentDiscountRate = this.activeReceiptDiscountRate || 0
      this.discountModalOpen = true
    },

    wholesaleKey () {
      return `shop_wholesale_view_${state.getCashRegister()?.id || 'default'}`
    },
    toggleWholesale () {
      this.wholesaleView = !this.wholesaleView
      localStorage.setItem(this.wholesaleKey(), this.wholesaleView)
    },

    openShopPdf (pdf) {
      const meta = this.receiptDocMeta || {}
      this.$refs.pdfViewer.open(pdf, {
        receiptId: meta.receiptId,
        documentType: meta.documentType || 'receipt',
        receiptMeta: {
          receiptNumber: meta.receiptNumber,
          total: meta.total,
          currency: meta.currency,
          type: meta.type
        }
      })
    },

    onColHandleMousedown (type, e) {
      this.colDrag = { type, startX: e.clientX, startCat: this.catWidth, startCart: this.cartWidth }
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      document.addEventListener('mousemove', this._onColMove)
      document.addEventListener('mouseup', this._onColEnd)
    },
    _onColMove (e) {
      if (!this.colDrag) return
      const dx = e.clientX - this.colDrag.startX
      if (this.colDrag.type === 'cat') {
        this.catWidth = Math.max(120, Math.min(480, this.colDrag.startCat + dx))
      } else {
        this.cartWidth = Math.max(260, Math.min(600, this.colDrag.startCart - dx))
      }
    },
    _onColEnd () {
      if (!this.colDrag) return
      localStorage.setItem(regKey('col_cat'), this.catWidth)
      localStorage.setItem(regKey('col_cart'), this.cartWidth)
      this.colDrag = null
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      document.removeEventListener('mousemove', this._onColMove)
      document.removeEventListener('mouseup', this._onColEnd)
    },

    onCartExpand () {
      this.cartExpanded = !this.cartExpanded
    },

    openCloseBalance () {
      this.$refs.closeBalance.open()
    },

    updateValueDate () {
      const days = Number(this.selectedPaymentDue)
      if (!days || isNaN(days)) return
      const date = new Date(this.invoiceDate)
      date.setDate(date.getDate() + days)
      this.valueDate = date.getTime()
    },

    tsToDateStr (ts) {
      return new Date(ts).toISOString().substr(0, 10)
    },

    dateStrToTs (str) {
      return str ? new Date(str).getTime() : Date.now()
    },

    async onPaymentModalCreate ({ method, fiscalize, paymentReference, eracun, eracunEmail }) {
      this.paymentMethod = method
      this.fiscalize = fiscalize
      this.customPnb = !!paymentReference
      this.paymentReference = paymentReference || ''
      if (eracun !== undefined) this.eracun = eracun
      if (eracunEmail !== undefined) this.eracunEmail = eracunEmail
      await this.createReceipt()
    },

    async getCashRegister () {
      const response = await df.doc(`cash_registers/${state.getCashRegister().id}`).get()
      if (!response.data()) throw new Error('Nije moguće dohvatiti podatke o blagajni')
      return response.data()
    },

    createReceipt () {
      if (this.cart.length === 0 || this.creatingReceipt) return
      if (['T', 'P'].includes(this.paymentMethod) && !this.customer) {
        this.noCustomerDialog = true
        return
      }
      this.proceedCreateReceipt()
    },

    async proceedCreateReceipt () {
      if (this.cart.length === 0 || this.creatingReceipt) return
      this.creatingReceipt = true

      try {
        const cashRegister = await this.getCashRegister()
        const currency = this.currency
        const method = this.paymentMethod
        const receiptNumber = method === 'P'
          ? (cashRegister.offer_sequence || 0) + 1
          : method === 'V'
            ? (cashRegister.bill_sequence || 0) + 1
            : (cashRegister.bill_sequence || 0) + 1

        const data = {
          currency,
          fullTotal: Math.round(this.fullTotal),
          items: this.cart.map(item => {
            const prices = item.prices || {}
            // Ako artikl nema taxes, kopiraj iz osnove ili postavi default
            if (prices[currency] && !prices[currency].taxes) {
              prices[currency].taxes = [{ rate: 2500, name: 'PDV 25%' }]
            }
            const mapped = {
              id: item.id,
              name: item.name,
              quantity: item.quantity,
              code: item.code || '',
              prices,
              selling_unit: item.selling_unit || item.unit || 'kom',
              type: item.type || 'product'
            }
            // Proslijedi popust i sniženu cijenu (u centima per unit) za remapItems
            if (item.discount) mapped.discount = item.discount
            if (item.discounted_price != null) mapped.discounted_price = item.discounted_price
            if (item.kpdcode) mapped.kpdcode = item.kpdcode
            if (item.naknade) mapped.naknade = item.naknade
            return mapped
          }),
          paymentConfig: {},
          registerType: cashRegister.type || 'MP',
          title: '',
          total: Math.round(this.total),
          totalShoppingCart: 0,
          totalWithDiscount: 0,
          type: method
        }

        if (this.discount) data.discount = this.discount

        const items = await remapItems(data, method, cashRegister)
        const paymentMethod = getPaymentMethod(method)
        const entity = method === 'P' ? 'offer' : method === 'V' ? 'virman' : 'receipt'
        const fiscalize = method === 'T' ? this.fiscalize : (method !== 'V' && method !== 'P')

        // Gradi type objekt s podacima o kupcu (ako postoji)
        // Flag po metodi, isto kao CrudTemplateDialog/templatePayment.js:
        // T -> 'R' (omogućuje payment_due u sendHttp-u), G/K/O -> 'BO',
        // V -> 'V', P -> 'P' (sendHttp ionako forsira 'P' za entity 'offer')
        const flagByMethod = { T: 'R', V: 'V', P: 'P' }
        const type = { flag: flagByMethod[method] || 'BO' }
        if (this.customer) {
          const c = this.customer
          const oib = c.oib || (c.oibs && c.oibs[0]) || ''
          type.payer_name = c.name || ''
          type.payer_oib = oib
          type.payer_address = c.address || ''
          type.payer_zip_code = c.zip_code || ''
          type.payer_city = c.city || ''
          type.payer_country = c.country || ''
        }

        const args = {
          entity,
          paymentMethod,
          paymentReference: this.customPnb ? this.paymentReference : this.buildAutoPaymentReference(receiptNumber, cashRegister),
          language: { value: 'hr' },
          fiscalize,
          type,
          receiptNumber
        }

        const payload = await getPayload(
          data,
          items,
          args,
          state,
          this.receiptNote ? { note: this.receiptNote } : null
        )

        // Datumi za T/V/P
        if (['T', 'V', 'P'].includes(method)) {
          payload.action.params.delivery_date = Math.round(this.deliveryDate / 1000)
          payload.action.params.payment_due = Math.round(this.valueDate / 1000)
        }

        if (this.eracun && this.eracunEnabled) {
          payload.action.params.eracun = true
          payload.action.params.eracun_email = this.eracunEmail
          const company = state.getCurrentCompany()
          if (company?.eracun?.provider) {
            payload.action.params.eracun_provider = company.eracun.provider
          }
        }

        const httpArgs = {
          issueDateTime: Math.round(this.invoiceDate / 1000),
          paymentMeth: paymentMethod,
          language: { value: 'hr' },
          type: '',
          receiptNumber,
          openPdfPrint: (pdf) => this.openShopPdf(pdf),
          hideLoader: this.hideLoader,
          removeOrderFromTable: () => {},
          fiscalize,
          msgBox: this.showMsgBox,
          confirm: this.confirm,
          confirmClose: this.confirmClose
        }

        // sendHttp inače nikad ne šalje delivery_date/payment_due receiptapi-ju.
        // delivery_date ide uvijek, bez obzira na metodu ili tip registra (MP
        // uključen). payment_due (datum valute) ide samo za T/P na VP ili ALL
        // registru.
        httpArgs.delivery_date = Math.round(this.deliveryDate / 1000)
        if ((cashRegister.type === 'VP' || cashRegister.type === 'ALL') && (method === 'T' || method === 'P')) {
          httpArgs.overrideValueDateTime = Math.round(this.valueDate / 1000)
        }

        sendHttp(data, items, payload, httpArgs, state.getSelectedPrinter(), { callNewPrint: true })
      } catch (err) {
        this.creatingReceipt = false
        this.hideLoader()
        this.showMsgBox({
          text: err && err.message ? err.message : 'Greška pri kreiranju računa',
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'error'
        })
      }
    },

    clearReceiptState () {
      this.cart = []
      this.customer = null
      this.discount = null
      this.activeReceiptDiscountRate = 0
      this.receiptNote = ''
      this.eracun = false
      this.eracunEmail = ''
      this.customPnb = false
      this.paymentReference = ''
      this.fiscalize = this.hasCertificate
      this.cartExpanded = false
      this.creatingReceipt = false
      // Vrati datum isporuke i datum valute na početne vrijednosti
      this.deliveryDate = Date.now()
      this.selectedPaymentDue = 8
      const d = new Date()
      d.setDate(d.getDate() + 8)
      this.valueDate = d.getTime()
    }
  },

  mounted () {
    this._onGlobalSearch = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        this.globalSearchOpen = true
      }
    }
    window.addEventListener('keydown', this._onGlobalSearch)
    this.getCategories()
    if (this.availablePaymentMethods.length) {
      this.paymentMethod = this.availablePaymentMethods[0].value
    }
    // Inicijaliziraj fiscalize prema certifikatu
    this.fiscalize = this.hasCertificate
    // Učitaj wholesale postavku za ovu specifičnu blagajnu
    this.wholesaleView = localStorage.getItem(this.wholesaleKey()) === 'true'

    // Uspješno kreiran račun — očisti košaricu
    this._onReceiptSuccess = () => { this.clearReceiptState() }
    EventBus.$on('payment-completed-success', this._onReceiptSuccess)

    this._onReceiptCreated = ({ receiptId, receiptNumber }) => {
      this.receiptDocMeta = {
        receiptId,
        receiptNumber,
        documentType: this.paymentMethod === 'P' ? 'ponuda' : 'receipt',
        total: this.fullTotal,
        currency: this.currency,
        eracun_email: this.eracunEmail || this.customer?.email || '',
        type: this.customer ? {
          payer_name: this.customer.name || '',
          payer_oib: this.customer.oib || (this.customer.oibs && this.customer.oibs[0]) || ''
        } : {}
      }
    }
    EventBus.$on('receiptCreated', this._onReceiptCreated)


    // Greška pri kreiranju — zadrži košaricu, skini loading, prikaži poruku
    this._onReceiptError = () => {
      this.creatingReceipt = false
      this.hideLoader()
      this.showMsgBoxHtml({
        text: '<b>Greška pri kreiranju računa</b><br>Molimo pokušajte ponovo. Košarica je sačuvana.',
        actions: ['cancel'],
        cancelBtnText: 'Zatvori',
        color: 'error'
      })
    }
    EventBus.$on('error-return', this._onReceiptError)

    const onSetMini = () => { /* margin handling if needed */ }
    EventBus.$on('set-mini', onSetMini)
    this._onSetMini = onSetMini
  },

  beforeDestroy () {
    this.listeners.forEach(l => l())
    this.listeners = []
    EventBus.$off('set-mini', this._onSetMini)
    EventBus.$off('payment-completed-success', this._onReceiptSuccess)
    EventBus.$off('error-return', this._onReceiptError)
    EventBus.$off('receiptCreated', this._onReceiptCreated)
    window.removeEventListener('keydown', this._onGlobalSearch)
    window.__shopCartCount = 0
    document.removeEventListener('mousemove', this._onColMove)
    document.removeEventListener('mouseup', this._onColEnd)
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
  }
}
</script>

<style scoped>
.shop-register {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 64px);
  overflow: hidden;
  background: #F8FAFC;
  position: relative;
  /* padding-right se postavlja dinamički via :style binding */
}
.col-resize-handle {
  width: 6px;
  flex-shrink: 0;
  cursor: col-resize;
  background: transparent;
  position: relative;
  z-index: 15;
  transition: background 0.15s;
}
.col-resize-handle::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 3px;
  height: 28px;
  background-image: radial-gradient(circle, rgba(148,163,184,0.55) 1.2px, transparent 1.2px);
  background-size: 3px 6px;
  background-repeat: repeat-y;
  border-radius: 2px;
  transition: background-image 0.15s;
  pointer-events: none;
}
.col-resize-handle:hover { background: rgba(0,188,212,0.14); }
.col-resize-handle:hover::after {
  background-image: radial-gradient(circle, rgba(0,188,212,0.85) 1.2px, transparent 1.2px);
}
.col-resize-handle--cart {
  position: absolute;
  top: 50px; /* ispod top-hdr */
  bottom: 0;
  width: 6px;
}
/* Full-width header iznad kategorija i artikala */
.top-hdr {
  background: #ffffff;
  border-bottom: 1px solid rgba(0,0,0,0.06);
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  padding: 0 14px;
  height: 50px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
/* Sadržaj ispod headera: kategorije + artikli */
.shop-content {
  flex: 1;
  display: flex;
  overflow: hidden;
}
.mid-loading-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  background: rgba(248, 250, 252, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}
.mid-loading-card {
  background: #ffffff;
  border: 1px solid rgba(0,0,0,0.06);
  border-radius: 14px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.08);
  padding: 32px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  min-width: 180px;
}
.mid-loading-amount {
  font-size: 26px;
  font-weight: 700;
  color: #00BCD4;
  letter-spacing: 0.3px;
}
.mid-loading-label {
  font-size: 12px;
  color: #B0BEC5;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}
.shop-mid {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}
.search-global-btn {
  display: flex; align-items: center; justify-content: space-between;
  width: 270px; flex-shrink: 0; margin-left: 8px;
  padding: 6px 11px;
  background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 10px;
  cursor: pointer; transition: all 0.15s;
}
.search-global-btn:hover { border-color: #00BCD4; background: rgba(0,188,212,0.04); }
.search-global-left { display: flex; align-items: center; gap: 5px; font-size: 12px; color: #94A3B8; }
.search-global-right { display: flex; align-items: center; gap: 3px; }
.search-kbd { font-size: 10px !important; color: #94A3B8 !important; background: #F1F5F9 !important; border: none !important; border-radius: 5px !important; padding: 2px 6px !important; white-space: nowrap; box-shadow: none !important; font-family: inherit !important; }
.mid-spacer { flex: 1; }
.pill-group { display: flex; gap: 4px; }
.pill--icon {
  width: 28px !important;
  height: 28px !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}
.pill {
  padding: 4px 10px; font-size: 11px; font-weight: 500;
  border-radius: 20px; border: 1.5px solid #E0E6ED;
  background: #fff; color: rgba(70,70,80,0.85);
  cursor: pointer; transition: all 0.15s; white-space: nowrap;
}
.pill:hover:not(:disabled) { border-color: #00BCD4; background: rgba(0,188,212,0.05); color: #00BCD4; }
.pill.active { border-color: #00BCD4; background: rgba(0,188,212,0.12); color: #00BCD4; font-weight: 600; }
.pill:disabled { opacity: 0.3; cursor: not-allowed; }
.vpc-tooltip { background: #ffffff !important; box-shadow: 0 4px 16px rgba(0,0,0,0.10) !important; border: 1.5px solid #00BCD4 !important; border-radius: 10px !important; padding: 0 !important; }
</style>
